import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Card, Link } from '@americanexpress/dls-react';
import { IconCardSwipe, IconP2p } from '@americanexpress/dls-icons';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import { useRouteParams } from '@/context/routeParamsContext';
import { useAnalyticsContext } from '@/context/analyticsContext';
import {
  cn,
  getCardArtUrl,
  splitAccessAndGuestPolicyHeader,
} from '@/helpers/utils';
import { cardArtMapping } from '@/constants/productMapping';
import { autoOpenedStatusSessionKey } from '@/constants';
import { getOneCmsLoungeFinderImagesBaseUrl } from '@/selectors/oneCmsSelectors';
import { getLocaleData } from '@/selectors/localeSelectors';
import {
  usePoliciesByLoungeId,
  useLoungeAccessListMatchedWithLocaleProviders,
  useIsWaitListEnabledLounge,
} from '@/hooks/loungesHooks/loungesHelperHooks';
import { useCommonContent } from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import {
  useSelectedProductFromUserWallet,
  useAutoOpenCardPickerStatus,
} from '@/hooks/memberHooks';
import { useModal } from '@/context/modalContext';
import styles from './LoungeDetails.module.scss';
import LoungeAccessProductDescription from '../common/LoungeAccessProductDescription';
import PolicyButton from '../common/PolicyButton';
import { modalTypes } from '../common/modals/constants';
import WaitListButton from '../waitList/WaitListButton';

const LoungeDetailsSidebar = ({ className }) => {
  const { product, loungeId } = useRouteParams();
  const { customTrack } = useAnalyticsContext();
  const { shouldShowCardPicker } = useAutoOpenCardPickerStatus();
  const { accessLevel, guestPolicy } =
    useLoungeAccessListMatchedWithLocaleProviders(loungeId);
  const {
    guestPolicyLabel: loungeSpecificGuestPolicyLabel,
    accessPolicyLabel: loungeSpecificAccessPolicyLabel,
  } = usePoliciesByLoungeId(loungeId);
  const {
    lounges: { accessPanel, agTab },
    selectCard,
  } = useCommonContent();
  const isWaitListAvailableAtLounge = useIsWaitListEnabledLounge(loungeId);
  const { setModalName } = useModal();

  const [accessPolicyHeaderText, guestPolicyHeaderText] =
    splitAccessAndGuestPolicyHeader(agTab);
  // below consts are for the card image swapping
  const { locale } = useSelector(getLocaleData);
  const baseOneCmsImgUrl = useSelector(getOneCmsLoungeFinderImagesBaseUrl);
  const urlSpecifiedProductFromUsersWallet =
    useSelectedProductFromUserWallet(product, locale) || {};
  const cardSwitchLinkText = selectCard?.link;
  const imageUrl = getCardArtUrl({
    baseOneCmsImgUrl,
    product: cardArtMapping[product],
    locale,
    isMedium: true,
  });
  const accessButtonId = uuidv4();
  const guestButtonId = uuidv4();

  useEffect(() => {
    if (
      window.sessionStorage.getItem(autoOpenedStatusSessionKey) !== '1' &&
      shouldShowCardPicker
    ) {
      setModalName(modalTypes.SELECT_CARD_MODAL);
    }
  }, [shouldShowCardPicker, setModalName]);

  const [isImageLoading, setIsImageLoading] = React.useState(true);

  // Reset image loading state when routeParams change (product or loungeId)
  useEffect(() => {
    setIsImageLoading(true);
    const img = new Image();
    img.src = urlSpecifiedProductFromUsersWallet?.cardArtUrl || imageUrl;

    if (img.complete) {
      setIsImageLoading(false);
    }
  }, [imageUrl, urlSpecifiedProductFromUsersWallet?.cardArtUrl]);

  const handleToggleModal = ({ modalName, ...trackingProps }) => {
    setModalName(modalName);
    customTrack(trackingProps);
  };

  return (
    <div
      className={cn(
        className,
        'margin-3-b',
        styles.loungeOverviewSidebarContainer
      )}
    >
      <div className={cn(styles.loungeOverviewSidebar, 'tw-overflow-hidden')}>
        <Card
          className={cn('tw-overflow-hidden', styles.accessPanelCardContainer)}
          aria-label="Card Callout Notification"
          style={{
            minWidth: 'unset',
            backgroundImage: `url(${urlSpecifiedProductFromUsersWallet?.cardArtUrl || imageUrl})`,
            backgroundSize: 'cover',
          }}
        >
          <div
            className="tw-backdrop-blur-1dot5xl"
            style={{ marginBlockEnd: '0' }}
          />
          <div className="tw-flex tw-justify-center tw-px-2">
            <img
              className={cn(
                isImageLoading && 'hidden',
                'tw-rounded-2xl tw-shadow-high sm_tw-mt-2'
              )}
              src={urlSpecifiedProductFromUsersWallet?.cardArtUrl || imageUrl}
              alt="Card Callout Notification"
              onLoad={() => setIsImageLoading(false)}
              onError={() => setIsImageLoading(false)}
            />
          </div>
        </Card>
        <div className="tw-bg-white">
          <div className="tw-p-6 tw-pb-1 tw-font-sans tw-text-xl">
            {Object.keys(urlSpecifiedProductFromUsersWallet).length > 0 && (
              <div className="tw-mb-3 tw-w-fit tw-rounded tw-bg-gradient-to-r tw-from-blue-deep-700 tw-to-blue-bright-shade-600-s8 tw-px-2 tw-py-1 tw-text-sm tw-font-bold tw-uppercase tw-text-white">
                {selectCard?.cardPill_loggedIn}
              </div>
            )}
            <LoungeAccessProductDescription locale={locale} product={product} />
          </div>
          <div className="tw-pb-4 tw-pl-6">
            <button
              id="select-card-button"
              type="button"
              className="tw-text-base tw-text-blue-bright-shade-600-s8 hover_tw-underline"
              /* TO-DO */
              onClick={(event) =>
                handleToggleModal({
                  event,
                  element: 'change card button',
                  detail: 'card call out notification',
                  modalName: modalTypes.SELECT_CARD_MODAL,
                })
              }
            >
              {cardSwitchLinkText}
            </button>
          </div>
          <hr
            className="tw-mx-6 tw-border-gray-300"
            aria-hidden="true"
            style={{ width: 'unset' }}
          />
          <div className="tw-px-4">
            {accessLevel && (
              <>
                <PolicyButton
                  id={`access-policy-${accessButtonId}`}
                  heading={
                    loungeSpecificAccessPolicyLabel || accessPolicyHeaderText
                  }
                  modalType={modalTypes.ACCESS_POLICY_MODAL}
                  icon={<IconCardSwipe size="md" />}
                />
                {/* only display another border if guest policy is present */}
                {guestPolicy && (
                  <hr
                    className="tw-mx-2 tw-border-gray-300"
                    aria-hidden="true"
                    style={{ width: 'unset' }}
                  />
                )}
              </>
            )}
            {guestPolicy && (
              <PolicyButton
                id={`guest-policy-${guestButtonId}`}
                heading={
                  loungeSpecificGuestPolicyLabel || guestPolicyHeaderText
                }
                modalType={modalTypes.GUEST_POLICY_MODAL}
                icon={<IconP2p size="md" />}
              />
            )}
          </div>
          <hr
            className="tw-mx-6 tw-border-gray-300"
            aria-hidden="true"
            style={{ width: 'unset' }}
          />
          <div className="margin-3-lr pad-2-t pad-2-b">
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- link to open the additional information modals */}
            <Link
              className="tw-font-medium tw-text-link"
              onClickCapture={(event) => {
                // Prevent child handlers from hijacking tracking info
                // to resolve issue where tracked events show generic
                // html element rather than the provided element/detail
                event.stopPropagation();
                handleToggleModal({
                  event,
                  element: 'terms and conditions',
                  modalName: modalTypes.TERMS_MODAL,
                });
              }}
            >
              {accessPanel?.link}
            </Link>
          </div>
        </div>
      </div>
      {isWaitListAvailableAtLounge && (
        <div
          className="tw-border-blue-deep-tint-t12 tw-rounded-2xl tw-bg-blue-deep-700 tw-bg-contain tw-bg-no-repeat"
          style={{
            backgroundImage: `url(${baseOneCmsImgUrl}amex-world-service-logo-accent.png)`,
          }}
        >
          <WaitListButton />
        </div>
      )}
    </div>
  );
};

LoungeDetailsSidebar.propTypes = {
  className: PropTypes.string,
};

export default LoungeDetailsSidebar;
